/**
 * One set of number formatters for the whole admin (audit 2026-10-10,
 * admin-05/13): en-US, "—" for missing values, never a fake 0.
 *
 * - `usd(v)`: money, 2 decimals with separators ($1,234.50).
 * - `usdUnit(v)`: unit costs (per call, per token): 2–3 significant digits so
 *   a cheap model reads $0.00031, not $0.00.
 * - `usdCompact(v)`: chart axes and tight tiles ($1.2K).
 */
const LOCALE = "en-US";

const money = new Intl.NumberFormat(LOCALE, { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const compact = new Intl.NumberFormat(LOCALE, { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 });
const integer = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 });

export const DASH = "—";

function isNum(v: number | null | undefined): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

export function usd(v: number | null | undefined): string {
  return isNum(v) ? money.format(v) : DASH;
}

export function usdUnit(v: number | null | undefined): string {
  if (!isNum(v)) return DASH;
  if (v === 0) return "$0";
  const abs = Math.abs(v);
  if (abs >= 1) return money.format(v);
  return new Intl.NumberFormat(LOCALE, {
    style: "currency",
    currency: "USD",
    minimumSignificantDigits: 2,
    maximumSignificantDigits: 3,
  }).format(v);
}

export function usdCompact(v: number | null | undefined): string {
  return isNum(v) ? (Math.abs(v) < 1000 ? usdUnit(v) : compact.format(v)) : DASH;
}

export function num(v: number | null | undefined): string {
  return isNum(v) ? integer.format(v) : DASH;
}

export function pct(v: number | null | undefined, digits = 1): string {
  return isNum(v) ? `${v.toFixed(digits)}%` : DASH;
}

const compactNumber = new Intl.NumberFormat(LOCALE, { notation: "compact", maximumFractionDigits: 1 });

/** Token and count volumes: 1.2K, 3.4M (exact below 1,000). */
export function compactNum(v: number | null | undefined): string {
  if (!isNum(v)) return DASH;
  return Math.abs(v) < 1000 ? integer.format(v) : compactNumber.format(v);
}

/** Milliseconds from a Firestore Timestamp-like ({ seconds }), an ISO string or a number. */
export function toMillis(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof value === "object" && "seconds" in value && typeof (value as { seconds: unknown }).seconds === "number") {
    return (value as { seconds: number }).seconds * 1000;
  }
  return null;
}

const dayFmt = new Intl.DateTimeFormat(LOCALE, { month: "short", day: "numeric", timeZone: "UTC" });
const dateTimeFmt = new Intl.DateTimeFormat(LOCALE, {
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "UTC",
});
const monthFmt = new Intl.DateTimeFormat(LOCALE, { month: "short", year: "numeric", timeZone: "UTC" });

/** "Oct 3" (UTC) from a `YYYY-MM-DD` key, an ISO date or a timestamp. */
export function shortDay(value: unknown): string {
  const ms =
    typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? Date.parse(`${value}T00:00:00Z`) : toMillis(value);
  return ms == null || Number.isNaN(ms) ? DASH : dayFmt.format(ms);
}

/** "Oct 3, 14:05 UTC": the one absolute date format of the admin (admin-08). */
export function dateTimeUtc(value: unknown): string {
  const ms = toMillis(value);
  return ms == null ? DASH : `${dateTimeFmt.format(ms)} UTC`;
}

/** "2026-10" → "Oct 2026". */
export function monthLabel(key: string): string {
  const ms = Date.parse(`${key}-01T00:00:00Z`);
  return Number.isNaN(ms) ? key : monthFmt.format(ms);
}

/** "3h ago" with the absolute UTC time for a title attribute. */
export function relativeTime(ms: number | null | undefined, now = Date.now()): { label: string; title: string } {
  if (!isNum(ms)) return { label: DASH, title: "" };
  const diff = Math.max(0, now - ms);
  const min = Math.round(diff / 60000);
  const label =
    min < 1 ? "just now" : min < 60 ? `${min}m ago` : min < 48 * 60 ? `${Math.round(min / 60)}h ago` : `${Math.round(min / 1440)}d ago`;
  return { label, title: new Date(ms).toISOString().replace("T", " ").slice(0, 16) + " UTC" };
}
